import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, CheckCheck } from 'lucide-react';

import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
  type AppNotification,
} from '../lib/notifications';
import { formatRelative } from '../lib/format';
import { t } from '../lib/i18n';
import { cn } from '../lib/cn';
import { Spinner } from './ui';

/** Campanita con badge de no leídas + dropdown de notificaciones. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const { data: unread } = useUnreadCount();
  const { data: notifications, isLoading } = useNotifications(open);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();

  const count = unread?.count ?? 0;
  const hasUnread = count > 0;

  const onClickItem = (n: AppNotification) => {
    if (!n.is_read) markRead.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link); // relativo: el basename lo aplica el router
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          data-tour="notifications"
          aria-label={t('notifications.title')}
          className="relative grid h-9 w-9 place-items-center rounded-token border border-line bg-surface-2 text-ink transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <Bell size={18} />
          {hasUnread && (
            <span
              aria-hidden
              className="absolute -right-1 -top-1 grid h-4 min-w-[1rem] place-items-center rounded-full bg-sun px-1 font-mono text-[10px] font-bold text-ink"
            >
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-[20rem] overflow-hidden rounded-token border border-line bg-surface p-0 shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-sm font-semibold text-ink">{t('notifications.title')}</span>
            {hasUnread && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="inline-flex items-center gap-1 rounded-token px-1.5 py-1 text-xs font-medium text-brand transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50"
              >
                <CheckCheck size={13} />
                {t('notifications.markAll')}
              </button>
            )}
          </div>

          <div className="max-h-[22rem] overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : notifications && notifications.length > 0 ? (
              notifications.map((n) => (
                <DropdownMenu.Item
                  key={n.id}
                  onSelect={(e) => {
                    e.preventDefault();
                    onClickItem(n);
                  }}
                  className={cn(
                    'flex cursor-pointer gap-2 border-b border-line px-3 py-2.5 text-sm outline-none last:border-b-0 data-[highlighted]:bg-surface-2',
                    n.is_read ? 'bg-surface' : 'bg-brand/5',
                  )}
                >
                  {!n.is_read && (
                    <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand" />
                  )}
                  <div className={cn('min-w-0 flex-1', n.is_read && 'pl-4')}>
                    <p className="truncate font-medium text-ink">{n.title}</p>
                    {n.body && <p className="line-clamp-2 text-xs text-muted">{n.body}</p>}
                    <p className="mt-0.5 text-[11px] text-muted">{formatRelative(n.created_at)}</p>
                  </div>
                </DropdownMenu.Item>
              ))
            ) : (
              <p className="px-3 py-8 text-center text-sm text-muted">
                {t('notifications.empty')}
              </p>
            )}
          </div>

          {/* Pie: ir a la pantalla de notificaciones (leer completas). */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/notificaciones');
            }}
            className="w-full border-t border-line px-3 py-2.5 text-center text-sm font-medium text-brand transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
          >
            {t('notifications.seeAll')}
          </button>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
